import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Badge,
} from "@mui/material";
import {
  Add as AddIcon,
  Hotel as HotelIcon,
  SquareFoot as SizeIcon,
  AttachMoney as MoneyIcon,
  Build as RepairIcon,
  Widgets as AmenityIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from "@mui/icons-material";
import Header from "./Header.js";
import { ManageRoomInfo } from "./ManageRoomInfo";
import { AddRepair } from "./AddRepair";
import { AddAmenity } from "./AddAmenity";
import { RoomImageModal } from "./RoomImageModal";

export const Rooms = () => {
  const [roomsDetails, setRoomsDetails] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [showRoomDetailsPopup, setShowRoomDetailsPopup] = useState(false);
  const [isAddRepair, setIsAddRepair] = useState(false);
  const [isAddAmenity, setIsAddAmenity] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRoomForMenu, setSelectedRoomForMenu] = useState(null);

  // ── image additions ──
  const [imageModalRoomId, setImageModalRoomId] = useState(null);
  const [roomImageCounts, setRoomImageCounts] = useState({}); // { [roomId]: number }
  // Bumped every time images change for a room, forces RoomThumbnailStrip to refetch
  const [thumbnailRefreshKeys, setThumbnailRefreshKeys] = useState({}); // { [roomId]: number }

  const url = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    getRoomsDetails();
  }, []);

  const getRoomsDetails = async () => {
    try {
      const response = await axios.get(`${url}/rooms/allDetails`);
      setRoomsDetails(response.data);
      fetchAllImageCounts(response.data.map((r) => r.id));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAllImageCounts = async (roomIds) => {
    const results = await Promise.allSettled(
      roomIds.map((id) =>
        axios.get(`${url}/images/getImagesByRoomId/${id}`).then((res) => ({ id, count: res.data.length }))
      )
    );
    const counts = {};
    results.forEach((r) => { if (r.status === "fulfilled") counts[r.value.id] = r.value.count; });
    setRoomImageCounts(counts);
  };

  const refreshRoomImageCount = async (roomId) => {
    try {
      const res = await axios.get(`${url}/images/getImagesByRoomId/${roomId}`);
      setRoomImageCounts((prev) => ({ ...prev, [roomId]: res.data.length }));
    } catch (e) { console.log(e); }
    // Bump the refresh key so RoomThumbnailStrip refetches and re-renders immediately
    setThumbnailRefreshKeys((prev) => ({ ...prev, [roomId]: (prev[roomId] || 0) + 1 }));
  };
  // ── end image additions ──

  const handleMenuClick = (event, roomId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRoomForMenu(roomId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRoomForMenu(null);
  };

  const handleAddAmenity = () => {
    setIsAddAmenity(true);
    setRoomId(selectedRoomForMenu);
    handleMenuClose();
  };

  const handleAddRepair = () => {
    setIsAddRepair(true);
    setRoomId(selectedRoomForMenu);
    handleMenuClose();
  };

  return (
    <>
      <Header />
      
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="xl">
          
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Room Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              onClick={() => {
                setShowRoomDetailsPopup(true);
                setRoomId(null);
              }}
              sx={{ mt: 2 }}
            >
              Add New Room
            </Button>
          </Box>

          {/* Rooms Grid */}
          <Grid container spacing={3}>
            {roomsDetails.map((room) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={room.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

                  {/* ── Thumbnail strip (new) ── */}
                  <RoomThumbnailStrip
                    roomId={room.id}
                    url={url}
                    onClick={() => setImageModalRoomId(room.id)}
                    refreshKey={thumbnailRefreshKeys[room.id] || 0}
                  />

                  <CardContent sx={{ flexGrow: 1 }}>
                    
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Room #{room.id} - {room.alias}
                        </Typography>
                      </Box>
                      
                      <IconButton 
                        size="small"
                        onClick={(e) => handleMenuClick(e, room.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    {/* Details */}
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HotelIcon fontSize="small" color="primary" />
                        <Typography variant="body" color="text.secondary">
                          Bed:
                        </Typography>
                        <Typography variant="body" fontWeight={600}>
                          {room.bedType}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SizeIcon fontSize="small" color="primary" />
                        <Typography variant="body" color="text.secondary">
                          Size:
                        </Typography>
                        <Typography variant="body" fontWeight={600}>
                          {room.size}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon fontSize="small" color="success" />
                        <Typography variant="body" color="text.secondary">
                          Weekly:
                        </Typography>
                        <Typography variant="body" fontWeight={600} color="success.main">
                          ${room.weeklyRate}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon fontSize="small" color="success" />
                        <Typography variant="body" color="text.secondary">
                          Daily:
                        </Typography>
                        <Typography variant="body" fontWeight={600} color="success.main">
                          ${room.dailyRate}
                        </Typography>
                      </Box>

                      {/* Warning */}
                      {room.rentable === 1 && (
                        <Chip
                          icon={<WarningIcon />}
                          label="Do not rent"
                          color="error"
                          size="small"
                        />
                      )}
                    </Stack>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ p: 1, pt: 0, flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setRoomId(room.id);
                          setShowRoomDetailsPopup(true);
                        }}
                      >
                        Edit
                      </Button>

                      {/* ── Images button (new) ── */}
                      <Badge badgeContent={roomImageCounts[room.id] || 0} color="primary">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PhotoLibraryIcon />}
                          onClick={() => setImageModalRoomId(room.id)}
                        >
                          Images
                        </Button>
                      </Badge>

                      <Badge badgeContent={room.repairsCount} color="error">
                        <Button
                          component={Link}
                          to={`/repairs/${room.id}`}
                          variant="outlined"
                          size="small"
                        >
                          Repairs
                        </Button>
                      </Badge>

                     
                    </Box>
                  </CardActions>
                  
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Empty State */}
          {roomsDetails.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <HotelIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No rooms yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click the button above to add your first room
              </Typography>
            </Box>
          )}
          
        </Container>
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleAddAmenity}>
          <AmenityIcon fontSize="small" sx={{ mr: 1 }} />
          Add Amenity
        </MenuItem>
        <MenuItem onClick={handleAddRepair}>
          <RepairIcon fontSize="small" sx={{ mr: 1 }} />
          Add Repair
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      {showRoomDetailsPopup && (
        <ManageRoomInfo
          value={roomId}
          getRoomsDetails={getRoomsDetails}
          setShowRoomDetailsPopup={setShowRoomDetailsPopup}
        />
      )}
      
      {isAddRepair && (
        <AddRepair 
          value={{ roomNumber: roomId }}
        />
      )}
      
      {isAddAmenity && (
        <AddAmenity 
          value={{ roomNumber: roomId }}
        />
      )}

      {/* ── Image modal (new) ── */}
      {imageModalRoomId && (
        <RoomImageModal
          roomId={imageModalRoomId}
          onClose={() => setImageModalRoomId(null)}
          onImagesChange={() => refreshRoomImageCount(imageModalRoomId)}
        />
      )}
    </>
  );
};

/* ── Thumbnail strip — shows ONLY the room's marketing image (if one is set) ── */
const RoomThumbnailStrip = ({ roomId, url, onClick, refreshKey }) => {
  const [marketingImage, setMarketingImage] = useState(null);

  useEffect(() => {
    axios
      .get(`${url}/images/getImagesByRoomId/${roomId}`)
      .then((res) => {
        const found = res.data.find((img) => img.isMarketing);
        setMarketingImage(found || null);
      })
      .catch(() => setMarketingImage(null));
  }, [roomId, url, refreshKey]); // refetch whenever refreshKey changes (e.g. after modal closes)

  if (!marketingImage) return null;

  return (
    <Box
      onClick={onClick}
      sx={{
        height: 100,
        overflow: 'hidden',
        cursor: 'pointer',
        borderRadius: '4px 4px 0 0',
        position: 'relative',
        '&:hover img': { filter: 'brightness(0.85)' },
      }}
    >
      <img
        src={marketingImage.imageUrl}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          transition: 'filter 0.2s',
        }}
      />
    </Box>
  );
};